import React, { useEffect, useState } from 'react';
import { fetchSqlData, fetchSqlDataWithQuery } from '../../../services/api';
import StandardTable, { createActionColumn } from '../../../components/common/StandardTable';
import { Card, Typography, Spin, Input, message } from 'antd';
import ToPlnP6TableDetails from './ToPlnP6TableDetails';

const { Title } = Typography;

const ToPlnP6DataTables: React.FC = () => {
  const [tableNames, setTableNames] = useState<{ object_name: string; object_type: string; row_count: number|null }[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTable, setSelectedTable] = useState<string|null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchTableNames = async () => {
      setLoading(true);
      try {
        const sqlQuery = `
          SELECT
            t.name AS object_name,
            'TABLE' AS object_type,
            SUM(p.rows) AS row_count
          FROM
            sys.tables t
          INNER JOIN
            sys.schemas s ON t.schema_id = s.schema_id
          LEFT JOIN
            sys.partitions p ON t.object_id = p.object_id AND p.index_id IN (0,1)
          WHERE
            s.name = 'dbo'
          GROUP BY
            t.name
          UNION ALL
          SELECT v.name AS object_name, 'VIEW' AS object_type,  NULL AS row_count
          FROM sys.views v
          INNER JOIN sys.schemas s ON v.schema_id = s.schema_id
          WHERE s.name = 'dbo' ORDER BY object_name;
        `;
        const result = await fetchSqlDataWithQuery(sqlQuery, 'PPMTRAIN');
        let rows: { object_name: string; object_type: string; row_count: number|null }[] = [];
        if (Array.isArray(result)) {
          if (result[0] && result[0].object_name) {
            rows = result;
          }
        }
        setTableNames(rows);
      } catch (err: any) {
        message.error('Failed to retrieve table names!');
      } finally {
        setLoading(false);
      }
    };
    fetchTableNames();
  }, []);

  type RowType = { object_name: string; object_type: string; row_count: number|null };
  const columns: any[] = [
    {
      title: 'Name',
      dataIndex: 'object_name',
      key: 'object_name',
      ellipsis: false,
      width: 600,
      sorter: (a: RowType, b: RowType) => a.object_name.localeCompare(b.object_name),
    },
    {
      title: 'Type',
      dataIndex: 'object_type',
      key: 'object_type',
      width: 100,
    },
    {
      title: 'Record Count',
      dataIndex: 'row_count',
      key: 'row_count',
      width: 150,
      sorter: (a: RowType, b: RowType) => (a.row_count || 0) - (b.row_count || 0),
      render: (value: number|null) => value !== null ? value.toLocaleString() : 'N/A',
      exportValue: (r: RowType) => (r.row_count ?? '')
    },
  ];

  if (selectedTable) {
    return (
      <ToPlnP6TableDetails
        tableName={selectedTable}
        onBack={() => setSelectedTable(null)}
      />
    );
  }

  const filteredTableNames = tableNames.filter((item) =>
    item.object_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Card>
      <Title level={3}>All Tables and Views in SQL Database</Title>
      <Input
        placeholder="Search table names"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ width: 250, marginBottom: 16 }}
      />
      {loading ? (
        <Spin />
      ) : (
        <StandardTable
          style={{ maxWidth: '100%', overflowX: 'auto' }}
          dataSource={filteredTableNames.map((row) => ({
            key: row.object_name,
            ...row
          }))}
          columns={[
            ...columns,
            createActionColumn({
              title: 'Actions',
              showView: true,
              showEdit: false,
              showDelete: false,
              onView: (record) => setSelectedTable(record.object_name),
              onEdit: (record) => message.info(`Edit: ${record.object_name}`),
              onDelete: (record) => message.info(`Delete: ${record.object_name}`),
            })
          ]}
          rowKey="key"
          bordered
        />
      )}
    </Card>
  );
};

export default ToPlnP6DataTables;
